/**
 * Error Boundary Component
 * Catches and handles React errors gracefully with structured error reporting
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { FaExclamationTriangle, FaRedo, FaBug } from 'react-icons/fa';

/**
 * Hardcoded locale strings for the ErrorBoundary fallback UI.
 *
 * We intentionally do NOT use react-i18next here: the ErrorBoundary is the
 * last line of defense when a React subtree crashes. If the i18n provider
 * itself is part of the broken tree, useTranslation() would fail and the
 * fallback would never render. navigator.language is dependency-free.
 */
const FALLBACK_LOCALES = {
  de: {
    title: 'Etwas ist schiefgelaufen',
    subtitle: 'Es tut uns leid, ein unerwarteter Fehler ist aufgetreten. Das Problem wurde protokolliert.',
    errorDetails: 'Fehlerdetails',
    errorId: 'Fehler-ID:',
    message: 'Nachricht:',
    tryAgain: 'Erneut versuchen',
    reportBug: 'Fehler melden',
    reload: 'Seite neu laden',
    contactSupport: 'Falls das Problem weiterhin besteht, kontaktieren Sie bitte den Support unter Angabe der Fehler-ID.',
    devDetails: 'Entwicklerdetails (zum Erweitern klicken)',
    stackTrace: 'Stack-Trace:',
    componentStack: 'Komponenten-Stack:',
    clipboardSuccess: 'Fehlerdetails in die Zwischenablage kopiert. Bitte fügen Sie sie in Ihrem Fehlerbericht ein.',
    clipboardFallback: (id: string) => `Fehler-ID: ${id}\n\nBitte fügen Sie diese in Ihren Fehlerbericht ein.`,
  },
  en: {
    title: 'Something went wrong',
    subtitle: "We're sorry, but something unexpected happened. The error has been logged.",
    errorDetails: 'Error Details',
    errorId: 'Error ID:',
    message: 'Message:',
    tryAgain: 'Try Again',
    reportBug: 'Report Bug',
    reload: 'Reload Page',
    contactSupport: 'If this problem persists, please contact support with the Error ID above.',
    devDetails: 'Development Details (Click to expand)',
    stackTrace: 'Stack Trace:',
    componentStack: 'Component Stack:',
    clipboardSuccess: 'Error details copied to clipboard. Please paste them in your bug report.',
    clipboardFallback: (id: string) => `Error ID: ${id}\n\nPlease include this in your bug report.`,
  },
} as const;

function pickLocale(): typeof FALLBACK_LOCALES.de | typeof FALLBACK_LOCALES.en {
  const lang = (typeof navigator !== 'undefined' ? navigator.language : 'en').toLowerCase();
  return lang.startsWith('de') ? FALLBACK_LOCALES.de : FALLBACK_LOCALES.en;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorId: ''
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorInfo: null,
      errorId
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Send error to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo);
    }
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    // In a real application, you would send this to your error reporting service
    const errorReport = {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      errorInfo: {
        componentStack: errorInfo.componentStack
      },
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // TODO: Send to backend error reporting endpoint when ready
    // fetch('/api/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(errorReport)
    // }).catch(console.error);
    void errorReport; // Suppress unused variable warning
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
  };

  private handleReportBug = () => {
    const bugReport = {
      errorId: this.state.errorId,
      error: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };

    const l = pickLocale();
    // Copy to clipboard for easy bug reporting
    navigator.clipboard.writeText(JSON.stringify(bugReport, null, 2))
      .then(() => {
        alert(l.clipboardSuccess);
      })
      .catch(() => {
        alert(l.clipboardFallback(bugReport.errorId));
      });
  };

  public render() {
    if (this.state.hasError) {
      // Render custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const l = pickLocale();
      return (
        <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <FaExclamationTriangle className="mx-auto h-16 w-16 text-red-500" />
              <h2 className="mt-6 text-3xl font-extrabold text-foreground">
                {l.title}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {l.subtitle}
              </p>
            </div>

            <div className="bg-card shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">{l.errorDetails}</h3>

              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                <div className="text-sm text-red-700">
                  <strong>{l.errorId}</strong> {this.state.errorId}
                </div>
                {this.state.error && (
                  <div className="text-sm text-red-700 mt-2">
                    <strong>{l.message}</strong> {this.state.error.message}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={this.handleRetry}
                  className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                >
                  <FaRedo className="mr-2" />
                  {l.tryAgain}
                </button>

                <button
                  type="button"
                  onClick={this.handleReportBug}
                  className="w-full flex justify-center items-center px-4 py-2 border border-border rounded-md shadow-sm text-sm font-medium text-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                >
                  <FaBug className="mr-2" />
                  {l.reportBug}
                </button>

                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="w-full flex justify-center items-center px-4 py-2 border border-border rounded-md shadow-sm text-sm font-medium text-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                >
                  {l.reload}
                </button>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 bg-muted rounded-md p-4">
                  <summary className="cursor-pointer text-sm font-medium text-foreground">
                    {l.devDetails}
                  </summary>
                  <div className="mt-2 text-xs text-muted-foreground font-mono whitespace-pre-wrap">
                    <div className="mb-2">
                      <strong>{l.stackTrace}</strong>
                      <br />
                      {this.state.error.stack}
                    </div>
                    {this.state.errorInfo && (
                      <div>
                        <strong>{l.componentStack}</strong>
                        <br />
                        {this.state.errorInfo.componentStack}
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>

            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                {l.contactSupport}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;